<template>
  <v-alert v-if="!arenaId" type="info" dense class="mb-4">
    {{ $t('entities.invoices.selectArenaReminder') }}
  </v-alert>
  <div v-else>
    <div class="d-flex align-center justify-space-between">
      <v-banner
        outlined
        rounded
        single-line
        color="grey"
        class="flex-grow-0 first-invoice"
        icon="mdi-exclamation">
        <template v-if="isLoadingOldestInvoice">
          {{ $t('entities.invoices.firstInvoiceDate') }}: ...
        </template>
        <template v-else>
          {{ $t('entities.invoices.firstInvoiceDate') }}:
          {{
            oldestInvoice ? formatDateValue(oldestInvoice.created_at) : ' - '
          }}
        </template>
      </v-banner>
      <CatalogSwitch
        :route="{ name: 'entities.invoices.transactions' }"
        :initial-value="$t('entities.invoices.title')"
        :options="[
          $t('entities.invoices.title'),
          $t('entities.invoices.transactions'),
        ]" />
    </div>
    <v-card>
      <v-row class="px-4 pt-4 mb-4" align="center">
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="pagination.size"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :disabled="!rowsPerPageItems.length || isLoadingInvoices"
            hide-details="auto"
            solo>
            <template v-slot:label>
              {{ $t('components.rowsPerPage.entries') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.sort"
            :items="sortOptions"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="isLoadingInvoices"
            hide-details="auto"
            item-text="text"
            item-value="value"
            solo>
            <template v-slot:label>
              {{ $t('miscellaneous.sortBy') }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filters.created_gte"
            type="date"
            solo
            hide-details="auto"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('miscellaneous.dateFrom') }}:
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filters.created_lt"
            type="date"
            solo
            hide-details="auto"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('miscellaneous.dateTo') }}:
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="filters.buyer_id"
            :items="buyerUsers"
            :loading="loadingBuyerUsers"
            item-text="displayName"
            item-value="id"
            no-filter
            solo
            hide-details="auto"
            :search-input.sync="buyerSearch"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('entities.invoices.filters.buyer') }}:
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="filters.seller_id"
            :items="sellerUsers"
            :loading="loadingSellerUsers"
            item-text="displayName"
            item-value="id"
            no-filter
            solo
            hide-details="auto"
            :search-input.sync="sellerSearch"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('entities.invoices.filters.seller') }}:
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.fiscalization_required"
            :items="fiscalizationRequiredOptions"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="isLoadingInvoices"
            hide-details="auto"
            item-text="text"
            item-value="value"
            solo
            clearable>
            <template v-slot:label>
              {{ $t('entities.invoices.filters.fiscalizationRequired') }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.has_manual_discount"
            :items="hasManualDiscountOptions"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="isLoadingInvoices"
            hide-details="auto"
            item-text="text"
            item-value="value"
            solo
            clearable>
            <template v-slot:label>
              {{ $t('miscellaneous.manualDiscount') }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="invoices"
        :items-per-page="pagination.size"
        :loading="isLoadingInvoices"
        :loading-text="$t('components.noData.fetchingEntries')"
        hide-default-footer>
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>{{
              $t('entities.invoices.title')
            }}</v-toolbar-title>
            <v-divider class="mx-4" inset vertical />
            <small class="mr-2">{{ pagination.count }}</small>
            <v-btn
              depressed
              x-small
              dark
              fab
              color="primary"
              :loading="isLoadingInvoices"
              :disabled="isLoadingInvoices"
              @click="refresh">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.payment_method_id="{ item }">
          {{ getPaymentMethodName(item.payment_method_id) }}
        </template>
        <template v-slot:item.total_gross_amount="{ item }">
          <span class="font-weight-bold">
            {{ formatGrossAmount(item.total_gross_amount) }}
          </span>
        </template>
        <template v-slot:item.manual_discount_amount="{ item }">
          {{ formatDiscountPct(item.manual_discount_amount) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDateValue(item.created_at) }}
        </template>
        <template v-slot:item.buyer_user_id="{ item }">
          <a
            v-if="usersById[item.buyer_user_id]"
            class="link primary--text"
            @click.stop="openUserModal(item.buyer_user_id)"
            v-text="getUserDisplayName(item.buyer_user_id)" />
          <span v-else>{{ getUserDisplayName(item.buyer_user_id) }}</span>
        </template>
        <template v-slot:item.fiscalized="{ item }">
          <v-chip
            :color="item.fiscalization_required ? 'red' : 'purple'"
            small
            dark
            pill>
            {{ item.fiscalization_required ? 'No' : 'Yes' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                class="ml-1"
                color="secondary"
                depressed
                x-small
                fab
                @click="openViewDialog(item)">
                <v-icon color="white" small>pageview</v-icon>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.invoices.viewEntity') }}</span>
            </v-card>
          </v-tooltip>
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                :disabled="printingInvoiceId === item.id"
                class="ml-1"
                color="primary"
                depressed
                x-small
                fab
                @click="printInvoice(item)">
                <template v-if="printingInvoiceId === item.id">
                  <ProgressCircular color="primary" />
                </template>
                <template v-else>
                  <v-icon color="white" small>print</v-icon>
                </template>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.invoices.printEntity') }}</span>
            </v-card>
          </v-tooltip>
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                :disabled="!!item.restoring_invoice_id"
                color="error"
                class="ml-1"
                depressed
                x-small
                fab
                @click="openRestoreDialog(item)">
                <v-icon color="white" small>restore</v-icon>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.invoices.restoreEntity') }}</span>
            </v-card>
          </v-tooltip>
          <template v-if="item.fiscalization_required && country !== 'ba'">
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  :disabled="fiscalizingInvoiceId === item.id"
                  class="ml-1"
                  color="purple"
                  depressed
                  x-small
                  fab
                  @click="fiscalize(item)">
                  <template v-if="fiscalizingInvoiceId === item.id">
                    <ProgressCircular color="purple" />
                  </template>
                  <template v-else>
                    <v-icon color="white" small>receipt</v-icon>
                  </template>
                </v-btn>
              </template>
              <v-card class="elevation-2 px-4 py-2">
                <span>{{ $t('entities.invoices.fiscalizeEntity') }}</span>
              </v-card>
            </v-tooltip>
          </template>
          <v-divider
            class="mx-1 my-auto vertical-divider-small"
            vertical
            inset></v-divider>
          <v-tooltip
            v-if="!userHasActiveSession(item.buyer_user_id)"
            color="transparent"
            top>
            <template v-slot:activator="{ on }">
              <v-btn
                @click="startPSSession(item)"
                v-on="on"
                :disabled="!arenaId || loadingSessions"
                color="success"
                class="ml-1"
                depressed
                x-small
                dark
                fab>
                <template v-if="loadingSessions">
                  <ProgressCircular color="success" />
                </template>
                <template v-else>
                  <v-icon class="white--text" small>mdi-controller</v-icon>
                </template>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.sessions.ps-start') }}</span>
            </v-card>
          </v-tooltip>
          <v-tooltip
            v-if="!userHasActiveSession(item.buyer_user_id)"
            color="transparent"
            top>
            <template v-slot:activator="{ on }">
              <v-btn
                @click="startSimulatorSession(item)"
                v-on="on"
                :disabled="!arenaId || loadingSessions"
                color="success"
                class="ml-1"
                depressed
                x-small
                dark
                fab>
                <template v-if="loadingSessions">
                  <ProgressCircular color="success" />
                </template>
                <template v-else>
                  <v-icon class="white--text" small>mdi-steering</v-icon>
                </template>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.sessions.sim-start') }}</span>
            </v-card>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="py-6 text-center grey--text">
            {{ $t('components.noData.noEntriesFound') }}
          </div>
        </template>
      </v-data-table>
      <v-layout
        v-if="pagination.count"
        class="text-center mt-3 pb-4"
        justify-center>
        <v-flex>
          <v-pagination
            v-model="pagination.page"
            :length="pagination.last_page"
            :total-visible="$vuetify.breakpoint.mdAndUp ? 10 : 5"
            :disabled="isLoadingInvoices"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right" />
          <h5 class="card-title font-weight-light mt-2">
            {{
              `${pagination.from} - ${pagination.to} ${$t(
                'components.pagination.of',
              )} ${pagination.count}`
            }}
          </h5>
        </v-flex>
      </v-layout>
      <RestoreDialog
        :value="restoreDialogOpen"
        :invoice="invoiceToRestore"
        :arena-name="arenaName"
        :currency-symbol="currencySymbol"
        :restoring="restoring"
        @input="restoreDialogOpen = $event"
        @restore="handleRestore" />
      <ViewDialog
        :value="viewDialogOpen"
        :invoice="invoiceToView"
        :arena-name="arenaName"
        :arena-id="arenaId"
        :currency-symbol="currencySymbol"
        :payment-methods-by-id="paymentMethodsById"
        :users-by-id="usersById"
        @input="viewDialogOpen = $event" />
      <div v-if="selectedUserId" style="display: none">
        <UserActions
          ref="userActions"
          :entity="userActionsEntity"
          :viewButton="false"
          :editButton="false"
          :shopButton="false"
          :awardButton="false"
          :simulateSessionButton="false"
          :sessionButton="false"
          :deleteButton="false" />
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Invoice from '@/models/shop/InvoiceV2'
import PaymentMethod from '@/models/shop/PaymentMethod'
import Currency from '@/models/shop/Currency'
import User from '@/models/users/User'
import Session from '@/models/main/Session'
import formatDate from '@/mixins/format-date'
import printInvoiceApi from '@/mixins/print-invoice-api'
import RestoreDialog from './RestoreDialog.vue'
import ViewDialog from './ViewDialog.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import CatalogSwitch from '@/components/CatalogSwitch.vue'
import UserActions from '@/components/UserActions.vue'
import { INVOICE_TABLE_HEADERS } from './headers'

const resetPagination = () => ({
  size: 10,
  from: 0,
  to: 0,
  page: 1,
  last_page: 0,
  count: 0,
})

export default {
  name: 'Invoices',
  components: {
    RestoreDialog,
    ViewDialog,
    ProgressCircular,
    CatalogSwitch,
    UserActions,
  },
  mixins: [formatDate, printInvoiceApi],
  data() {
    return {
      arenaCurrency: null,
      filters: {
        sort: '-created_at',
        created_gte: '',
        created_lt: '',
        buyer_id: null,
        seller_id: null,
        fiscalization_required: null,
        has_manual_discount: null,
      },
      buyerUsers: [],
      loadingBuyerUsers: false,
      buyerSearch: '',
      buyerSearchDebounce: null,
      sellerUsers: [],
      loadingSellerUsers: false,
      sellerSearch: '',
      sellerSearchDebounce: null,
      fiscalizingInvoiceId: null,
      printingInvoiceId: null,
      loadingSessions: false,
      sessions: [],
      invoices: [],
      isLoadingInvoices: false,
      pagination: resetPagination(),
      paymentMethodsById: {},
      usersById: {},
      restoreDialogOpen: false,
      invoiceToRestore: null,
      restoring: false,
      viewDialogOpen: false,
      invoiceToView: null,
      selectedUserId: null,
      oldestInvoice: null,
      isLoadingOldestInvoice: false,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters?.arena?.id
    },
    country() {
      return this.$store.getters.country
    },
    arenaName() {
      return this.$store.getters?.arena?.name ?? ''
    },
    rowsPerPageItems() {
      return [5, 10, 25, 50]
    },
    currencySymbol() {
      if (!this.arenaCurrency) return ''
      return this.arenaCurrency.symbol || this.arenaCurrency.code
    },
    headers() {
      return INVOICE_TABLE_HEADERS.map((header) => ({
        ...header,
        text: this.$t(header.text),
      }))
    },
    sortOptions() {
      return [
        {
          text: this.$t('entities.products.filters.newest'),
          value: '-created_at',
        },
        {
          text: this.$t('entities.products.filters.oldest'),
          value: 'created_at',
        },
      ]
    },
    fiscalizationRequiredOptions() {
      return [
        {
          text: this.$t('entities.invoices.filters.fiscalizationRequiredYes'),
          value: true,
        },
        {
          text: this.$t('entities.invoices.filters.fiscalizationRequiredNo'),
          value: false,
        },
      ]
    },
    hasManualDiscountOptions() {
      return [
        {
          text: this.$t('miscellaneous.yes'),
          value: true,
        },
        {
          text: this.$t('miscellaneous.no'),
          value: false,
        },
      ]
    },
    userActionsEntity() {
      // Pass only the id so UserActions fetches and normalizes the user
      // (identity/phone/email defaults) via setLocalEntity. Passing the raw
      // user (which has a username but no nested objects) makes
      // openViewDialog skip that normalization and the dialog fails to render.
      return { id: this.selectedUserId }
    },
    userHasActiveSession() {
      return (buyerUserId) => {
        if (!buyerUserId) {
          return true
        }
        const session = this.sessions.find((s) => s.user?.id === buyerUserId)
        return session || false
      }
    },
  },
  async created() {
    await Promise.all([this.fetchPaymentMethods(), this.fetchCurrency()])
    this.fetchInvoices()
    this.fetchOldestInvoice()
    this.fetchSessions()
  },
  methods: {
    ...mapActions(['showSnackbar']),
    openViewDialog(invoice) {
      this.invoiceToView = invoice
      this.viewDialogOpen = true
    },
    openUserModal(userId) {
      if (!userId) return
      this.selectedUserId = userId
      this.$nextTick(() => {
        this.$refs.userActions?.openViewDialog?.()
      })
    },
    openRestoreDialog(invoice) {
      this.invoiceToRestore = invoice
      this.restoreDialogOpen = true
    },
    async fiscalize(invoice) {
      if (!this.arenaId || !invoice?.id) return
      this.fiscalizingInvoiceId = invoice.id
      try {
        await Invoice.api.fisclize(this.arenaId, invoice.id)
        this.showSnackbar({
          color: 'success',
          message: this.$t('entities.invoices.notifications.fiscalize'),
        })
        this.refresh()
      } catch (error) {
        const data = error?.response?.data
        let message = data?.message ?? 'Failed to fiscalize invoice.'
        const errors = []
        let iconAndColor = 'error'
        if (error?.response?.status === 422) {
          iconAndColor = 'warning'
          if (data?.errors) {
            for (const i in data.errors) {
              errors.push(`${i}: ${data.errors[i]}`)
            }
            message += ' ' + errors.join(', ')
          }
        }
        if (error?.response?.status === 500 && data?.errors) {
          for (const i in data.errors) {
            errors.push(data.errors[i])
          }
          message += ' ' + errors.join(', ')
        }
        this.showSnackbar({
          color: iconAndColor,
          message,
          timeout: 10000,
          icon: iconAndColor,
        })
      } finally {
        this.fiscalizingInvoiceId = null
      }
    },
    async handleRestore(payload) {
      if (!this.arenaId || !this.invoiceToRestore?.id) return
      this.restoring = true
      try {
        await Invoice.api.restoreInvoice(
          this.arenaId,
          this.invoiceToRestore.id,
          payload,
        )
        this.showSnackbar({
          color: 'success',
          message: this.$t('entities.invoices.notifications.restore'),
        })
        this.restoreDialogOpen = false
        this.invoiceToRestore = null
        this.refresh()
      } catch (error) {
        const data = error?.response?.data
        let message = data?.message ?? 'Failed to restore invoice.'
        const errors = []
        let iconAndColor = 'error'
        if (error?.response?.status === 422) {
          iconAndColor = 'warning'
          if (data?.errors) {
            for (const i in data.errors) {
              errors.push(`${i}: ${data.errors[i]}`)
            }
            message += ' ' + errors.join(', ')
          }
        }
        if (error?.response?.status === 500 && data?.errors) {
          for (const i in data.errors) {
            errors.push(data.errors[i])
          }
          message += ' ' + errors.join(', ')
        }
        this.showSnackbar({
          color: iconAndColor,
          message,
          timeout: 10000,
          icon: iconAndColor,
        })
        this.restoreDialogOpen = false
        this.invoiceToRestore = null
        this.refresh()
      } finally {
        this.restoring = false
      }
    },
    getPaymentMethodName(id) {
      if (!id) return '/'
      return this.paymentMethodsById[id]?.name || id
    },
    getUserDisplayName(buyerUserId) {
      if (!buyerUserId) return '/'
      const user = this.usersById[buyerUserId]
      if (!user) return '/'
      if (user.identity?.name) return user.identity.name
      if (user?.name) return user?.name
      if (user.username) return user.username
      return '/'
    },
    formatDateValue(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return this.formatDate(parsed, true, true)
    },
    formatGrossAmount(amount) {
      return amount ? `${amount} ${this.currencySymbol}` : '-'
    },
    formatDiscountPct(amount) {
      return amount ? `${amount} %` : '-'
    },
    resetState() {
      this.invoices = []
      this.paymentMethodsById = {}
      this.usersById = {}
      this.pagination = resetPagination()
      this.arenaCurrency = null
      this.filters.sort = '-created_at'
      this.filters.created_gte = ''
      this.filters.created_lt = ''
      this.filters.buyer_id = null
      this.filters.seller_id = null
      this.filters.fiscalization_required = null
      this.filters.has_manual_discount = null
      this.buyerUsers = []
      this.buyerSearch = ''
      this.sellerUsers = []
      this.sellerSearch = ''
      this.oldestInvoice = null
    },
    async fetchBuyerUsers(query = '') {
      try {
        this.loadingBuyerUsers = true
        const params = this.getBuyerUserRequestParams(query)
        const response = await User.api.get(params)
        const data = response.data?.content || []
        this.buyerUsers = data.map((user) => ({
          ...user,
          displayName: user.identity?.name
            ? `${user.identity.name} (${user.username})`
            : user.username,
        }))
      } catch (error) {
        console.warn('Searching for buyer users failed.', error)
        this.buyerUsers = []
      } finally {
        this.loadingBuyerUsers = false
      }
    },
    getBuyerUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const searchParams = this.getBuyerSearchParams(query)
      return `size=10&page=1&${searchParams}`
    },
    getBuyerSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    async fetchSellerUsers(query = '') {
      try {
        this.loadingSellerUsers = true
        const params = this.getSellerUserRequestParams(query)
        const response = await User.api.get(params)
        const data = response.data?.content || []
        this.sellerUsers = data.map((user) => ({
          ...user,
          displayName: user.identity?.name
            ? `${user.identity.name} (${user.username})`
            : user.username,
        }))
      } catch (error) {
        console.warn('Searching for seller users failed.', error)
        this.sellerUsers = []
      } finally {
        this.loadingSellerUsers = false
      }
    },
    getSellerUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const searchParams = this.getSellerSearchParams(query)
      return `size=10&page=1&${searchParams}`
    },
    getSellerSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    setPaginationTotals(metadata = null, dataLength = 0) {
      const currentPage = this.pagination.page || 1
      const size = this.pagination.size || 10
      const count = metadata?.count ?? dataLength ?? 0
      const lastPage = size ? Math.ceil((count || 0) / size) : 0
      const from = count ? (currentPage - 1) * size + 1 : 0
      const to = count ? Math.min(from + size - 1, count) : 0
      this.pagination.from = from
      this.pagination.to = to
      this.pagination.page = currentPage
      this.pagination.last_page = lastPage
      this.pagination.count = count
    },
    async fetchInvoices() {
      if (!this.arenaId) {
        this.resetState()
        return
      }
      this.isLoadingInvoices = true
      try {
        const { page, size } = this.pagination
        const params = {
          page,
          size,
          sort: this.filters.sort,
        }
        if (this.filters.created_gte) {
          params.created_gte = `${this.filters.created_gte}T00:00:00`
        }
        if (this.filters.created_lt) {
          const next = new Date(this.filters.created_lt)
          next.setDate(next.getDate() + 1)
          const y = next.getFullYear()
          const m = String(next.getMonth() + 1).padStart(2, '0')
          const d = String(next.getDate()).padStart(2, '0')
          params.created_lt = `${y}-${m}-${d}T00:00:00`
        }
        if (this.filters.buyer_id) {
          params.buyer_id = this.filters.buyer_id
        }
        if (this.filters.seller_id) {
          params.seller_id = this.filters.seller_id
        }
        if (this.filters.fiscalization_required !== null) {
          params.fiscalization_required = this.filters.fiscalization_required
        }
        if (this.filters.has_manual_discount !== null) {
          params.has_manual_discount = this.filters.has_manual_discount
        }
        const response = await Invoice.api.getArenaInvoices(
          this.arenaId,
          params,
        )
        const payload = this.unpackResponse(response)
        this.invoices = payload.map((dto) => new Invoice(dto))
        const metadata = response?.data?.metadata || {}
        this.setPaginationTotals(metadata, this.invoices.length)
        await this.fetchUsersForInvoices()
      } catch (error) {
        console.error('Failed to fetch invoices.', error)
        this.resetState()
      } finally {
        this.isLoadingInvoices = false
      }
    },
    async fetchOldestInvoice() {
      if (!this.arenaId) {
        this.oldestInvoice = null
        return
      }
      this.isLoadingOldestInvoice = true
      try {
        const params = {
          page: 1,
          size: 1,
          sort: 'created_at',
        }
        const response = await Invoice.api.getArenaInvoices(
          this.arenaId,
          params,
        )
        const payload = this.unpackResponse(response)
        this.oldestInvoice = payload.length > 0 ? new Invoice(payload[0]) : null
      } catch (error) {
        console.error('Failed to fetch oldest invoice.', error)
        this.oldestInvoice = null
      } finally {
        this.isLoadingOldestInvoice = false
      }
    },
    async fetchPaymentMethods() {
      try {
        const response = await PaymentMethod.api.list()
        const paymentMethods = this.unpackResponse(response).map(
          (dto) => new PaymentMethod(dto),
        )
        this.paymentMethodsById = paymentMethods.reduce((acc, method) => {
          acc[method.id] = method
          return acc
        }, {})
      } catch (error) {
        console.error('Failed to fetch payment methods.', error)
        this.paymentMethodsById = {}
      }
    },
    async fetchCurrency() {
      if (!this.arenaId) {
        this.arenaCurrency = null
        return
      }
      try {
        const response = await Currency.api.getArenaCurrencies(this.arenaId)
        const dataArray = response.data?.data ?? []
        const currencies = dataArray.map((it) => new Currency(it))
        this.arenaCurrency = currencies[0] || null
      } catch (error) {
        console.error('Failed to fetch currency.', error)
        this.arenaCurrency = null
      }
    },
    async fetchUsersForInvoices() {
      const buyerUserIds = [
        ...new Set(
          this.invoices
            .map((invoice) => invoice.buyer_user_id)
            .filter((id) => id && id !== 0),
        ),
      ]

      if (buyerUserIds.length === 0) {
        this.usersById = {}
        return
      }

      try {
        const userPromises = []
        for (let i = 0; i < buyerUserIds.length; i += 100) {
          const chunk = buyerUserIds.slice(i, i + 100)
          userPromises.push(User.api.findByIds(chunk))
        }

        const usersResponses = await Promise.all(userPromises)
        const users = usersResponses.flatMap((response) => response.data || [])

        this.usersById = users.reduce((acc, user) => {
          acc[user.id] = user
          return acc
        }, {})
      } catch (error) {
        console.error('Failed to fetch users for invoices.', error)
        this.usersById = {}
      }
    },
    refresh() {
      this.pagination.page = 1
      this.fetchInvoices()
      this.fetchSessions()
    },
    async startPSSession(invoice) {
      if (!invoice?.buyer_user_id || !this.arenaId) {
        return
      }

      const user = this.usersById[invoice.buyer_user_id]
      if (!user) {
        return
      }

      try {
        this.loadingSessions = true

        const data = {
          arena_id: this.arenaId,
          device_id: 'ps',
          username: user.username,
          user_id: user.id,
        }
        await Session.api
          .create(this.arenaId, data)
          .then(() => {
            this.fetchSessions()
          })
          .catch((error) => {
            this.loadingSessions = false
            // Server error
            if (error.response.status === 500) {
              this.showSnackbar({
                color: 'error',
                message: error.response.data,
              })
            } else {
              this.showSnackbar({
                color: 'error',
                message: this.$t(
                  `entities.sessions.errorCodes.${error.response.data.code}`,
                ),
              })
            }
          })
      } catch (error) {
        console.warn(`Start PlayStation Arena Session API failed.`)
        console.log(error)
      } finally {
        this.loadingSessions = false
      }
    },
    async startSimulatorSession(invoice) {
      if (!invoice?.buyer_user_id || !this.arenaId) {
        return
      }

      const user = this.usersById[invoice.buyer_user_id]
      if (!user) {
        return
      }

      try {
        this.loadingSessions = true

        const data = {
          arena_id: this.arenaId,
          device_id: 'sim',
          username: user.username,
          user_id: user.id,
        }
        await Session.api
          .create(this.arenaId, data)
          .then(() => {
            this.fetchSessions()
          })
          .catch((error) => {
            this.loadingSessions = false
            // Server error
            if (error.response.status === 500) {
              this.showSnackbar({
                color: 'error',
                message: error.response.data,
              })
            } else {
              this.showSnackbar({
                color: 'error',
                message: this.$t(
                  `entities.sessions.errorCodes.${error.response.data.code}`,
                ),
              })
            }
          })
      } catch (error) {
        console.warn(`Start Simulator Arena Session API failed.`)
        console.log(error)
      } finally {
        this.loadingSessions = false
      }
    },
    async fetchSessions() {
      if (!this.arenaId) {
        this.sessions = []
        return
      }
      try {
        this.loadingSessions = true
        const response = await Session.api.getByArena(
          this.arenaId,
          `active=true&size=100`,
        )
        this.sessions = response.data.data
      } catch (e) {
        console.warn(`Arena Sessions API failed.`)
        console.log(e)
      } finally {
        this.loadingSessions = false
      }
    },
    onFirstPage() {
      return this.pagination.page === 1
    },
    setPageToOne() {
      this.pagination.page = 1
    },
    unpackResponse(response) {
      const payload = response?.data?.data ?? response?.data ?? []
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      return []
    },
  },
  watch: {
    arenaId(newArenaId, oldArenaId) {
      if (newArenaId === oldArenaId) return
      if (!newArenaId) {
        this.resetState()
        this.sessions = []
        this.oldestInvoice = null
        return
      }
      this.resetState()
      this.fetchCurrency()
      this.fetchInvoices()
      this.fetchOldestInvoice()
      this.fetchSessions()
    },
    'pagination.size'(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchInvoices()
      } else {
        this.setPageToOne()
      }
    },
    'pagination.page'(newVal, oldVal) {
      if (newVal === oldVal) return
      this.fetchInvoices()
    },
    'filters.sort'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.created_gte'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.created_lt'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.buyer_id'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.seller_id'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.fiscalization_required'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    'filters.has_manual_discount'() {
      this.pagination.page = 1
      this.fetchInvoices()
    },
    buyerSearch(newVal) {
      if (this.buyerSearchDebounce) {
        clearTimeout(this.buyerSearchDebounce)
      }
      if (newVal && newVal.trim()) {
        this.buyerSearchDebounce = setTimeout(() => {
          this.fetchBuyerUsers(newVal)
        }, 400)
      } else {
        this.buyerUsers = []
      }
    },
    sellerSearch(newVal) {
      if (this.sellerSearchDebounce) {
        clearTimeout(this.sellerSearchDebounce)
      }
      if (newVal && newVal.trim()) {
        this.sellerSearchDebounce = setTimeout(() => {
          this.fetchSellerUsers(newVal)
        }, 400)
      } else {
        this.sellerUsers = []
      }
    },
  },
}
</script>

<style scoped>
.first-invoice {
  margin-top: -64px;
}

.link {
  cursor: pointer;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.vertical-divider-small {
  height: 24px !important;
  align-self: center;
}
</style>
